import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Search, Loader2, ArrowRight, Building2, Globe, Mail, Phone, RefreshCw, CheckCircle2, AlertCircle, Clock, MapPin, Briefcase, SlidersHorizontal, Filter, Users, XCircle, Target, Zap, TrendingUp, ShoppingCart, Eye, QrCode, Check, ChevronsUpDown, Map as MapIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { VCardQRButton } from '../components/VCardQRModal';
import MapAreaSearch from '../components/MapAreaSearch';

// Region suggestions for combobox
const REGION_SUGGESTIONS = [
  { group: "Überregional", items: [
    { value: "DACH", label: "DACH (DE, AT, CH)" },
    { value: "Deutschland", label: "Deutschland (gesamt)" },
    { value: "Österreich", label: "Österreich" },
    { value: "Schweiz", label: "Schweiz" },
  ]},
  { group: "Bundesländer", items: [
    { value: "Baden-Württemberg", label: "Baden-Württemberg" },
    { value: "Bayern", label: "Bayern" },
    { value: "Berlin", label: "Berlin" },
    { value: "Brandenburg", label: "Brandenburg" },
    { value: "Bremen", label: "Bremen" },
    { value: "Hamburg", label: "Hamburg" },
    { value: "Hessen", label: "Hessen" },
    { value: "Mecklenburg-Vorpommern", label: "Mecklenburg-Vorpommern" },
    { value: "Niedersachsen", label: "Niedersachsen" },
    { value: "Nordrhein-Westfalen", label: "Nordrhein-Westfalen" },
    { value: "Rheinland-Pfalz", label: "Rheinland-Pfalz" },
    { value: "Saarland", label: "Saarland" },
    { value: "Sachsen", label: "Sachsen" },
    { value: "Sachsen-Anhalt", label: "Sachsen-Anhalt" },
    { value: "Schleswig-Holstein", label: "Schleswig-Holstein" },
    { value: "Thüringen", label: "Thüringen" },
  ]},
  { group: "Beliebte Regionen", items: [
    { value: "Harz", label: "Harz" },
    { value: "Ruhrgebiet", label: "Ruhrgebiet" },
    { value: "Schwarzwald", label: "Schwarzwald" },
    { value: "Bodensee", label: "Bodensee-Region" },
    { value: "Erzgebirge", label: "Erzgebirge" },
    { value: "Allgäu", label: "Allgäu" },
    { value: "Münsterland", label: "Münsterland" },
    { value: "Franken", label: "Franken" },
    { value: "Rhein-Main-Gebiet", label: "Rhein-Main-Gebiet" },
    { value: "Sauerland", label: "Sauerland" },
    { value: "Lausitz", label: "Lausitz" },
  ]},
];

function RegionCombobox({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const displayValue = value || '';

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && search.trim()) {
      // Check if search matches a suggestion
      const allItems = REGION_SUGGESTIONS.flatMap(g => g.items);
      const match = allItems.find(i => i.value.toLowerCase() === search.trim().toLowerCase());
      onChange(match ? match.value : search.trim());
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" data-testid={testId}>
          {displayValue ? (
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
              {displayValue}
            </span>
          ) : (
            <span className="text-muted-foreground">Region eingeben oder auswählen...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="z.B. Harz, Ruhrgebiet, Bayern..."
            value={search}
            onValueChange={setSearch}
            onKeyDown={handleInputKeyDown}
          />
          <CommandList>
            {search.trim() && !REGION_SUGGESTIONS.flatMap(g => g.items).some(i => i.label.toLowerCase().includes(search.toLowerCase())) && (
              <CommandItem onSelect={() => handleSelect(search.trim())} className="text-primary">
                <Search className="w-3.5 h-3.5 mr-2" />
                "{search.trim()}" als Region verwenden
              </CommandItem>
            )}
            {REGION_SUGGESTIONS.map(group => (
              <CommandGroup key={group.group} heading={group.group}>
                {group.items.map(item => (
                  <CommandItem key={item.value} value={item.label} onSelect={() => handleSelect(item.value)}>
                    {item.value === value && <Check className="w-3.5 h-3.5 mr-2 text-primary" />}
                    {item.value !== value && <span className="w-3.5 mr-2" />}
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
            <CommandEmpty>
              {search.trim() ? (
                <button className="w-full text-left px-2 py-1.5 text-sm text-primary hover:underline cursor-pointer" onClick={() => handleSelect(search.trim())}>
                  "{search.trim()}" als Region verwenden
                </button>
              ) : 'Keine Vorschläge'}
            </CommandEmpty>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const PHASE_ORDER = ['starting', 'analyzing', 'researching', 'scoring', 'completed'];
const AUTO_DISCOVERY_KEY = 'discovery_auto_started';

const LEAD_STATUS_OPTIONS = [
  { value: 'lead_generated', label: 'Lead generiert', color: 'bg-slate-100 text-slate-700' },
  { value: 'contacted', label: 'Kontakt aufgenommen', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'no_interest', label: 'Kein Interesse', color: 'bg-red-100 text-red-700' },
  { value: 'follow_up', label: 'Wiedervorlage', color: 'bg-amber-100 text-amber-700' },
];

const getStatusConfig = (status) => {
  if (!status) return { label: 'Status wählen', color: 'bg-white border-dashed border-slate-300 text-slate-400' };
  return LEAD_STATUS_OPTIONS.find(s => s.value === status) || { label: 'Status wählen', color: 'bg-white border-dashed border-slate-300 text-slate-400' };
};

function ProgressTracker({ phase, detail }) {
  const currentIdx = PHASE_ORDER.indexOf(phase);
  const phases = [
    { key: 'analyzing', label: 'Analyse' },
    { key: 'researching', label: 'Recherche' },
    { key: 'scoring', label: 'Bewertung' },
    { key: 'completed', label: 'Fertig' },
  ];

  return (
    <Card className="mb-6 border-primary/20 bg-primary/5" data-testid="discovery-progress">
      <CardContent className="p-6">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Search className="w-6 h-6 text-primary animate-pulse" />
          </div>
          <div>
            <p className="font-semibold">KI-Kundenrecherche aktiv</p>
            <p className="text-sm text-muted-foreground">{detail || 'Wird verarbeitet...'}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {phases.map((p, i) => {
            const pIdx = PHASE_ORDER.indexOf(p.key);
            const isDone = currentIdx >= pIdx;
            const isActive = PHASE_ORDER[currentIdx] === p.key;
            return (
              <React.Fragment key={p.key}>
                <div className="flex flex-col items-center gap-1.5 flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-500 ${
                    isDone ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  } ${isActive && !isDone ? 'ring-2 ring-primary/40 ring-offset-2' : ''}`}>
                    {isDone && p.key !== PHASE_ORDER[currentIdx] ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : isActive ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className={`text-xs text-center ${isDone ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{p.label}</span>
                </div>
                {i < phases.length - 1 && (
                  <div className={`h-0.5 flex-1 rounded-full mt-[-18px] transition-colors duration-500 ${currentIdx > pIdx ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DiscoveryPage() {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRun, setActiveRun] = useState(null);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filterBundesland, setFilterBundesland] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterCompanySize, setFilterCompanySize] = useState('');
  const [showFilterHint, setShowFilterHint] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [addonLoading, setAddonLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [pendingSearchBody, setPendingSearchBody] = useState(null);
  const [showMapSearch, setShowMapSearch] = useState(false);
  const pollRef = useRef(null);
  const autoStartedRef = useRef(false);

  // Auto-start discovery when no accounts exist and no run is active
  const autoStartDiscovery = useCallback(async () => {
    if (autoStartedRef.current) return;
    autoStartedRef.current = true;
    try {
      const res = await api.post('/discovery/start', {});
      const { run_id, status } = res.data;
      if (status === 'running') {
        setActiveRun({ id: run_id, status: 'running', progress_phase: 'starting', progress_detail: 'Automatische Kundenrecherche gestartet...' });
        startPolling(run_id);
        toast.info('Kundenrecherche wird automatisch gestartet...');
      }
    } catch (err) {
      console.error('Auto-start discovery failed:', err);
      autoStartedRef.current = false;
    }
  }, []);

  const load = useCallback(async (skipAutoStart = false) => {
    try {
      const runsRes = await api.get('/discovery/runs');
      const loadedRuns = (runsRes.data.runs || []);
      setRuns(loadedRuns);
      
      const running = loadedRuns.find(r => r.status === 'running');
      if (running) {
        setActiveRun(running);
        startPolling(running.id);
      } else {
        // Auto-select the latest completed run
        const latestCompleted = loadedRuns.find(r => r.status === 'completed');
        if (latestCompleted) {
          await selectRun(latestCompleted.id);
        } else if (!skipAutoStart && !autoStartedRef.current) {
          // First visit: show filter popup instead of auto-starting
          autoStartedRef.current = true;
          setShowFilterHint(true);
        }
      }
    } catch {}
    setLoading(false);
    setInitialLoadDone(true);
  }, [autoStartDiscovery]);

  const selectRun = async (runId) => {
    setSelectedRunId(runId);
    try {
      const res = await api.get(`/discovery/runs/${runId}`);
      setAccounts(res.data.accounts || []);
    } catch {
      setAccounts([]);
    }
  };

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load]);

  const startPolling = (runId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/discovery/runs/${runId}/status`);
        const data = res.data;
        setActiveRun(prev => ({ ...prev, ...data }));
        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setActiveRun(null);
          if (data.hint_message) {
            if (data.accounts_found === 0) {
              toast.error(data.hint_message, { duration: 8000 });
            } else {
              toast.warning(`${data.accounts_found} Leads gefunden. ${data.hint_message}`, { duration: 8000 });
            }
          } else {
            toast.success(`Suche abgeschlossen! ${data.accounts_found} Leads gefunden.`);
          }
          // Reload runs list and auto-select the completed run
          const runsRes = await api.get('/discovery/runs');
          const loadedRuns = runsRes.data.runs || [];
          setRuns(loadedRuns);
          // Select the just-completed run
          setSelectedRunId(runId);
          try {
            const runRes = await api.get(`/discovery/runs/${runId}`);
            setAccounts(runRes.data.accounts || []);
          } catch {}
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setActiveRun(null);
          toast.error('Suche fehlgeschlagen: ' + (data.progress_detail || 'Unbekannter Fehler'));
          load();
        }
      } catch {}
    }, 2000);
  };

  // Handle 403 limit reached — show upgrade/addon dialog
  const handleLimitReached = async () => {
    try {
      const res = await api.get('/discovery/usage');
      setUsageData(res.data);
      setShowLimitDialog(true);
    } catch {
      toast.error('Nutzungsdaten konnten nicht geladen werden');
    }
  };

  // Buy lead add-on (one-time payment)
  const buyLeadAddon = async () => {
    setAddonLoading(true);
    try {
      const res = await api.post('/billing/lead-addon-checkout', { origin_url: window.location.origin });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout konnte nicht erstellt werden');
    }
    setAddonLoading(false);
  };

  // Check for returning from addon checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const addonSessionId = params.get('addon_session_id');
    if (addonSessionId) {
      (async () => {
        try {
          const res = await api.get(`/billing/lead-addon-status/${addonSessionId}`);
          if (res.data.status === 'paid') {
            toast.success(`${res.data.leads_amount} zusätzliche Leads freigeschaltet!`);
          }
        } catch {}
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
      })();
    }
  }, []);

  // Generic function to start discovery (handles duplicate + limit)
  const tryStartDiscovery = async (body, isForced = false) => {
    try {
      const payload = isForced ? { ...body, force: true } : body;
      const res = await api.post('/discovery/start', payload);
      const { status, run_id, existing_run } = res.data;

      if (status === 'duplicate' && !isForced) {
        setDuplicateWarning({ existingRun: existing_run, body });
        return;
      }

      if (status === 'running') {
        setActiveRun({ id: run_id, status: 'running', progress_phase: 'starting', progress_detail: 'Suche wird gestartet...' });
        startPolling(run_id);
        toast.info('Kundenrecherche gestartet...');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        handleLimitReached();
      } else {
        toast.error(err.response?.data?.detail || 'Suche konnte nicht gestartet werden');
      }
    }
  };

  // Repeat a previous run with the exact same filters (dedup excludes already-found leads)
  // Polling helper for area-search runs (used by both the initial map search
  // and the "Diese Suche wiederholen" button on previous area runs).
  const startAreaRunPolling = (runId) => {
    setActiveRun({ id: runId, status: 'running', progress_phase: 'starting' });
    setShowMapSearch(false);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/discovery/runs/${runId}/status`);
        const d = res.data;
        setActiveRun(prev => prev ? { ...prev, ...d } : prev);
        if (d.status === 'completed') {
          clearInterval(pollRef.current); pollRef.current = null;
          setActiveRun(null);
          const msg = d.hint_message ? `${d.accounts_found} Leads. ${d.hint_message}` : `Karten-Suche abgeschlossen! ${d.accounts_found} Leads gefunden.`;
          if (d.accounts_found > 0) toast.success(msg); else toast.warning(msg);
          const [rsRes, accsRes] = await Promise.all([api.get('/discovery/runs'), api.get(`/discovery/accounts?run_id=${runId}`)]);
          setRuns(rsRes.data.runs || []);
          setAccounts(accsRes.data.accounts || []);
          setSelectedRunId(runId);
        } else if (d.status === 'failed') {
          clearInterval(pollRef.current); pollRef.current = null;
          setActiveRun(null);
          toast.error(d.progress_detail || 'Karten-Suche fehlgeschlagen');
        }
      } catch (e) { /* swallow */ }
    }, 2500);
  };

  const repeatRun = async (run) => {
    if (activeRun || !run) return;

    // ── Karten-Suche separat behandeln ──
    if (run.source === 'area_search') {
      if (!run.area_shape) {
        toast.error('Diese Karten-Suche kann nicht automatisch wiederholt werden — bitte Bereich neu auf der Karte einzeichnen.');
        setShowMapSearch(true);
        return;
      }
      toast.info(`Karten-Suche wird wiederholt — bereits gefundene Leads werden ausgelassen...`);
      try {
        const res = await api.post('/discovery/area-search', {
          shape: run.area_shape,
          branche: run.filters_industry || '',
          grid_radius_m: run.area_grid_radius_m || 8000,
        });
        if (res.data?.run_id) startAreaRunPolling(res.data.run_id);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Karten-Suche konnte nicht gestartet werden');
      }
      return;
    }

    // ── Normale Filter-Suche ──
    const filterParts = [run.filters_bundesland, run.filters_region, run.filters_industry, run.filters_company_size].filter(Boolean);
    const desc = filterParts.length ? filterParts.join(', ') : 'gleichen Filtern';
    toast.info(`Suche wird mit ${desc} wiederholt — bereits gefundene Leads werden ausgelassen...`);
    await tryStartDiscovery({
      bundesland: run.filters_bundesland || '',
      region: run.filters_region || '',
      industry: run.filters_industry || '',
      company_size: run.filters_company_size || '',
    }, true);
  };

  // Start new discovery (for main button - shows filter hint first)
  const refreshWithNewLeads = async () => {
    if (activeRun) return;
    setFilterBundesland('');
    setFilterRegion('');
    setFilterIndustry('');
    setFilterCompanySize('');
    setShowFilterHint(true);
  };

  const startWithFilters = async () => {
    setShowFilterHint(false);
    const body = {};
    if (filterBundesland.trim()) body.bundesland = filterBundesland.trim();
    if (filterRegion.trim()) body.region = filterRegion.trim();
    if (filterIndustry.trim()) body.industry = filterIndustry.trim();
    if (filterCompanySize && filterCompanySize !== 'all') body.company_size = filterCompanySize;
    await tryStartDiscovery(body);
  };

  const startWithoutFilters = async () => {
    setShowFilterHint(false);
    await tryStartDiscovery({});
  };

  const openNewSearch = () => {
    setFilterBundesland('');
    setFilterRegion('');
    setFilterIndustry('');
    setFilterCompanySize('');
    setShowFilterDialog(true);
  };

  const startDiscovery = async () => {
    setShowFilterDialog(false);
    const body = {};
    if (filterBundesland.trim()) body.bundesland = filterBundesland.trim();
    if (filterRegion.trim()) body.region = filterRegion.trim();
    if (filterIndustry.trim()) body.industry = filterIndustry.trim();
    if (filterCompanySize && filterCompanySize !== 'all') body.company_size = filterCompanySize;
    await tryStartDiscovery(body);
  };

  const updateLeadStatus = async (accountId, newStatus, e) => {
    e.stopPropagation(); // Prevent row click
    try {
      await api.put(`/discovery/accounts/${accountId}/status`, { status: newStatus });
      // Update local state
      setAccounts(prev => prev.map(acc => 
        acc.id === accountId ? { ...acc, lead_status: newStatus } : acc
      ));
      const statusLabel = LEAD_STATUS_OPTIONS.find(s => s.value === newStatus)?.label || newStatus;
      toast.success(`Status geändert: ${statusLabel}`);
    } catch (err) {
      toast.error('Status konnte nicht aktualisiert werden');
    }
  };

  const scoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-500';
  };

  const scoreBarColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="discovery-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Kundenrecherche</h1>
          <p className="text-muted-foreground mt-1">Potenzielle B2B-Kunden finden und priorisieren</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowMapSearch(v => !v)} disabled={!!activeRun} data-testid="toggle-map-search-btn" className={showMapSearch ? 'border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100' : ''}>
            <MapIcon className="w-4 h-4 mr-2" /> {showMapSearch ? 'Karten-Suche schließen' : 'Karten-Suche'}
          </Button>
          <Button variant="outline" onClick={openNewSearch} disabled={!!activeRun} data-testid="filter-search-btn">
            <Filter className="w-4 h-4 mr-2" /> Mit Filtern suchen
          </Button>
          <Button onClick={refreshWithNewLeads} disabled={!!activeRun} size="lg" data-testid="start-discovery-btn">
            {activeRun ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Suche läuft...</span>
            ) : (
              <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4" />Neue Leads generieren</span>
            )}
          </Button>
        </div>
      </div>

      {showMapSearch && (
        <Card className="mb-6" data-testid="map-search-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapIcon className="w-5 h-5 text-emerald-600" /> Karten-Suche
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Zeichne einen Bereich auf der Karte ein (Polygon, Rechteck oder Kreis) und wir suchen Unternehmen genau aus diesem Gebiet.
            </p>
          </CardHeader>
          <CardContent>
            <MapAreaSearch onRunStarted={(runId) => startAreaRunPolling(runId)} />
          </CardContent>
        </Card>
      )}

      {activeRun && (
        <ProgressTracker
          phase={activeRun.progress_phase || 'starting'}
          detail={activeRun.progress_detail}
        />
      )}

      {runs.length > 0 && !activeRun && (
        <div className="flex items-center gap-3 mb-4 overflow-x-auto pb-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Suchläufe:</span>
          {runs.filter(r => r.status === 'completed').map(run => {
            const isSelected = selectedRunId === run.id;
            return (
              <div key={run.id} className={`flex items-center rounded-full overflow-hidden border transition-all hover:shadow-md ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'}`}>
                <Badge
                  variant={isSelected ? 'default' : 'secondary'}
                  className="whitespace-nowrap cursor-pointer rounded-r-none border-0 shadow-none"
                  onClick={() => selectRun(run.id)}
                  data-testid={`run-badge-${run.id}`}>
                  <Clock className="w-3 h-3 mr-1" />
                  {new Date(run.created_at).toLocaleDateString('de-DE')} — {run.accounts_found} Leads
                  {run.filters_region && <span className="ml-1 opacity-70">({run.filters_region})</span>}
                  {run.filters_industry && <span className="ml-1 opacity-70">({run.filters_industry})</span>}
                </Badge>
                <button
                  onClick={(e) => { e.stopPropagation(); repeatRun(run); }}
                  className="px-2 py-1 text-xs bg-background hover:bg-muted text-muted-foreground hover:text-foreground border-l flex items-center gap-1 transition-colors"
                  title="Diese Suche wiederholen (bereits gefundene Leads werden übersprungen)"
                  data-testid={`run-repeat-${run.id}`}
                >
                  <RefreshCw className="w-3 h-3" /> Wiederholen
                </button>
              </div>
            );
          })}
        </div>
      )}

      {accounts.length === 0 && !activeRun ? (
        <Card>
          <CardContent className="py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Erste Kundenrecherche starten</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Starten Sie Ihre erste Lead-Suche. Mit Filtern (Region, Branche, Unternehmensgröße) erhalten Sie deutlich passendere Ergebnisse.
            </p>
            <Button onClick={refreshWithNewLeads} data-testid="start-first-discovery-btn">
              <Search className="w-4 h-4 mr-2" /> Suche starten
            </Button>
          </CardContent>
        </Card>
      ) : accounts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-lg" data-testid="leads-title">
                Gefundene Leads ({accounts.length})
                {selectedRunId && runs.length > 0 && (() => {
                  const run = runs.find(r => r.id === selectedRunId);
                  if (!run) return null;
                  const parts = [];
                  if (run.filters_region) parts.push(run.filters_region);
                  if (run.filters_industry) parts.push(run.filters_industry);
                  return parts.length > 0 ? <span className="text-sm font-normal text-muted-foreground ml-2">— {parts.join(', ')}</span> : null;
                })()}
              </CardTitle>
              {selectedRunId && (() => {
                const run = runs.find(r => r.id === selectedRunId);
                if (!run) return null;
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => repeatRun(run)}
                    disabled={!!activeRun}
                    data-testid="repeat-selected-run-btn"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Diese Suche wiederholen
                  </Button>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Unternehmen</TableHead>
                    <TableHead>Branche</TableHead>
                    <TableHead>Standort</TableHead>
                    <TableHead>Umsatz</TableHead>
                    <TableHead>Telefon</TableHead>
                    <TableHead className="text-center">Match</TableHead>
                    <TableHead className="w-[160px]">Status</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map(acc => {
                    const currentStatus = acc.lead_status || '';
                    const statusConfig = getStatusConfig(currentStatus);
                    const phoneNumber = acc.contact_info?.general_phone || acc.contact_info?.contact_phone || acc.contact_info?.phone || '';
                    return (
                      <TableRow key={acc.id} className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => navigate(`/discovery/accounts/${acc.id}`)}
                        data-testid={`account-row-${acc.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <Building2 className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{acc.company_name}</p>
                              {acc.match_reasoning && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1 max-w-[280px]">{acc.match_reasoning}</p>}
                              {!acc.match_reasoning && (acc.employees_bucket || acc.employee_count) && <p className="text-xs text-muted-foreground">{acc.employees_bucket || `${acc.employee_count} Mitarbeiter`}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell><span className="text-sm">{acc.industry}</span></TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">{acc.location || '—'}</span>
                          {acc.location_verified && <span className="inline-flex items-center ml-1 text-emerald-600 text-xs" title="Aus Impressum verifiziert">&#10003;</span>}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const rd = acc.revenue_data || {};
                            const ba = acc.bundesanzeiger || {};
                            const fmt = (eur) => eur >= 1_000_000_000
                              ? `${(eur/1_000_000_000).toFixed(2).replace(/\.?0+$/,'')} Mrd €`
                              : eur >= 1_000_000
                                ? `${(eur/1_000_000).toFixed(1).replace(/\.0$/,'')} Mio €`
                                : `${(eur/1000).toFixed(0)}k €`;
                            const check = (conf, label) => {
                              if (conf === 'primary') return <span className="inline-flex items-center ml-1 text-emerald-600 text-xs" title={label || 'Primärquelle'}>&#10003;</span>;
                              if (conf === 'secondary') return <span className="inline-flex items-center ml-1 text-amber-500 text-xs" title={label || 'Sekundärquelle'}>&#10003;</span>;
                              return null;
                            };
                            if (rd.verified && rd.value_eur) {
                              return (<span className="text-sm font-medium">{fmt(rd.value_eur)}{check(rd.confidence || 'secondary', rd.source_label)}</span>);
                            }
                            if (ba.found && ba.revenue_eur) {
                              return (<span className="text-sm font-medium">{fmt(ba.revenue_eur)}{check('primary', 'Bundesanzeiger')}</span>);
                            }
                            if (rd.estimate_low && rd.estimate_high) {
                              return (<span className="text-sm text-muted-foreground" title={rd.basis || ''}>~ {fmt(rd.estimate_low)}–{fmt(rd.estimate_high)}</span>);
                            }
                            return (<span className="text-sm text-muted-foreground">{acc.revenue || '—'}</span>);
                          })()}
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          {phoneNumber ? (
                            <a href={`tel:${phoneNumber}`} className="flex items-center gap-1.5 text-sm text-primary hover:underline" data-testid={`phone-${acc.id}`}>
                              <Phone className="w-3.5 h-3.5" />
                              <span className="truncate max-w-[140px]">{phoneNumber}</span>
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                              <div className={`h-full rounded-full ${scoreBarColor(acc.match_score)}`} style={{ width: `${acc.match_score}%` }} />
                            </div>
                            <span className={`text-sm font-semibold ${scoreColor(acc.match_score)}`}>{acc.match_score}</span>
                          </div>
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Select value={currentStatus} onValueChange={(val) => updateLeadStatus(acc.id, val, { stopPropagation: () => {} })}>
                            <SelectTrigger className={`h-8 text-xs ${statusConfig.color}`} data-testid={`status-select-${acc.id}`}>
                              <SelectValue placeholder="Status wählen" />
                            </SelectTrigger>
                            <SelectContent>
                              {LEAD_STATUS_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  <span className={`px-2 py-0.5 rounded text-xs ${opt.color}`}>{opt.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-0.5">
                            <VCardQRButton lead={acc} />
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`view-account-${acc.id}`}
                              onClick={() => navigate(`/discovery/accounts/${acc.id}`)}>
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter Dialog */}
      {/* Filter Hint Popup - shown when clicking main "Entdecken" button */}
      <Dialog open={showFilterHint} onOpenChange={setShowFilterHint}>
        <DialogContent className="sm:max-w-md" data-testid="filter-hint-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-emerald-600" /> Bessere Ergebnisse mit Filtern
            </DialogTitle>
            <DialogDescription>
              Suchen mit mindestens einem Filter liefern deutlich passendere Leads. Wählen Sie z.B. eine Region oder Branche.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-muted-foreground" /> Bundesland
              </Label>
              <Select value={filterBundesland || 'all'} onValueChange={v => setFilterBundesland(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="hint-filter-bundesland">
                  <SelectValue placeholder="Alle Bundesländer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Bundesländer</SelectItem>
                  <SelectItem value="Baden-Württemberg">Baden-Württemberg</SelectItem>
                  <SelectItem value="Bayern">Bayern</SelectItem>
                  <SelectItem value="Berlin">Berlin</SelectItem>
                  <SelectItem value="Brandenburg">Brandenburg</SelectItem>
                  <SelectItem value="Bremen">Bremen</SelectItem>
                  <SelectItem value="Hamburg">Hamburg</SelectItem>
                  <SelectItem value="Hessen">Hessen</SelectItem>
                  <SelectItem value="Mecklenburg-Vorpommern">Mecklenburg-Vorpommern</SelectItem>
                  <SelectItem value="Niedersachsen">Niedersachsen</SelectItem>
                  <SelectItem value="Nordrhein-Westfalen">Nordrhein-Westfalen</SelectItem>
                  <SelectItem value="Rheinland-Pfalz">Rheinland-Pfalz</SelectItem>
                  <SelectItem value="Saarland">Saarland</SelectItem>
                  <SelectItem value="Sachsen">Sachsen</SelectItem>
                  <SelectItem value="Sachsen-Anhalt">Sachsen-Anhalt</SelectItem>
                  <SelectItem value="Schleswig-Holstein">Schleswig-Holstein</SelectItem>
                  <SelectItem value="Thüringen">Thüringen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-muted-foreground" /> Region / Stadt <span className="text-xs text-muted-foreground">(optional)</span>
              </Label>
              <RegionCombobox value={filterRegion} onChange={setFilterRegion} testId="hint-filter-region" />
              <p className="text-xs text-muted-foreground">z.B. "Harz", "Ruhrgebiet", "Göttingen" — für gezieltere Ergebnisse</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Briefcase className="w-4 h-4 text-muted-foreground" /> Branche
              </Label>
              <Input
                placeholder="z.B. Landschaftsbau, Metallverarbeitung, IT"
                value={filterIndustry}
                onChange={e => setFilterIndustry(e.target.value)}
                data-testid="hint-filter-industry"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-muted-foreground" /> Unternehmensgröße
              </Label>
              <Select value={filterCompanySize || 'all'} onValueChange={v => setFilterCompanySize(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="hint-filter-size">
                  <SelectValue placeholder="Alle Größen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Größen</SelectItem>
                  <SelectItem value="1-10 Mitarbeiter">1-10 Mitarbeiter</SelectItem>
                  <SelectItem value="11-50 Mitarbeiter">11-50 Mitarbeiter</SelectItem>
                  <SelectItem value="51-250 Mitarbeiter">51-250 Mitarbeiter</SelectItem>
                  <SelectItem value="251-1000 Mitarbeiter">251-1000 Mitarbeiter</SelectItem>
                  <SelectItem value="1000+ Mitarbeiter">1000+ Mitarbeiter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="ghost" className="text-muted-foreground" onClick={startWithoutFilters} data-testid="skip-filter-btn">
              Ohne Filter fortfahren
            </Button>
            <Button onClick={startWithFilters} disabled={!filterBundesland && !filterRegion && !filterIndustry.trim() && (!filterCompanySize || filterCompanySize === 'all')} data-testid="apply-filter-btn">
              <Search className="w-4 h-4 mr-2" /> Mit Filter suchen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filtered Search Dialog (from "Mit Filtern suchen" button) */}
      <Dialog open={showFilterDialog} onOpenChange={setShowFilterDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" /> Gefilterte Suche
            </DialogTitle>
            <DialogDescription>
              Grenzen Sie die Suche nach Region, Branche oder Unternehmensgröße ein.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-muted-foreground" /> Bundesland
              </Label>
              <Select value={filterBundesland || 'all'} onValueChange={v => setFilterBundesland(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="filter-bundesland-select">
                  <SelectValue placeholder="Alle Bundesländer" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Bundesländer</SelectItem>
                  <SelectItem value="Baden-Württemberg">Baden-Württemberg</SelectItem>
                  <SelectItem value="Bayern">Bayern</SelectItem>
                  <SelectItem value="Berlin">Berlin</SelectItem>
                  <SelectItem value="Brandenburg">Brandenburg</SelectItem>
                  <SelectItem value="Bremen">Bremen</SelectItem>
                  <SelectItem value="Hamburg">Hamburg</SelectItem>
                  <SelectItem value="Hessen">Hessen</SelectItem>
                  <SelectItem value="Mecklenburg-Vorpommern">Mecklenburg-Vorpommern</SelectItem>
                  <SelectItem value="Niedersachsen">Niedersachsen</SelectItem>
                  <SelectItem value="Nordrhein-Westfalen">Nordrhein-Westfalen</SelectItem>
                  <SelectItem value="Rheinland-Pfalz">Rheinland-Pfalz</SelectItem>
                  <SelectItem value="Saarland">Saarland</SelectItem>
                  <SelectItem value="Sachsen">Sachsen</SelectItem>
                  <SelectItem value="Sachsen-Anhalt">Sachsen-Anhalt</SelectItem>
                  <SelectItem value="Schleswig-Holstein">Schleswig-Holstein</SelectItem>
                  <SelectItem value="Thüringen">Thüringen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Target className="w-4 h-4 text-muted-foreground" /> Region / Stadt <span className="text-xs text-muted-foreground">(optional)</span>
              </Label>
              <RegionCombobox value={filterRegion} onChange={setFilterRegion} testId="filter-region-input" />
              <p className="text-xs text-muted-foreground">z.B. "Harz", "Ruhrgebiet", "Göttingen" — für gezieltere Ergebnisse</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Briefcase className="w-4 h-4 text-muted-foreground" /> Branchensuche (wie Google)
              </Label>
              <Input
                placeholder="z.B. Kranhersteller, Softwareentwicklung, Metallverarbeitung"
                value={filterIndustry}
                onChange={e => setFilterIndustry(e.target.value)}
                data-testid="filter-industry-input"
              />
              <p className="text-xs text-muted-foreground">Geben Sie einen Suchbegriff ein - die KI findet passende Unternehmen</p>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-muted-foreground" /> Unternehmensgröße
              </Label>
              <Select value={filterCompanySize} onValueChange={setFilterCompanySize}>
                <SelectTrigger data-testid="filter-company-size-select">
                  <SelectValue placeholder="Alle Größen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Größen</SelectItem>
                  <SelectItem value="1-10 Mitarbeiter">1-10 Mitarbeiter (Kleinstunternehmen)</SelectItem>
                  <SelectItem value="11-50 Mitarbeiter">11-50 Mitarbeiter (Kleinunternehmen)</SelectItem>
                  <SelectItem value="51-250 Mitarbeiter">51-250 Mitarbeiter (Mittelstand)</SelectItem>
                  <SelectItem value="251-1000 Mitarbeiter">251-1000 Mitarbeiter (Größerer Mittelstand)</SelectItem>
                  <SelectItem value="1000+ Mitarbeiter">1000+ Mitarbeiter (Großunternehmen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowFilterDialog(false)}>Abbrechen</Button>
            <Button onClick={startDiscovery} data-testid="confirm-start-discovery-btn">
              <Search className="w-4 h-4 mr-2" /> Suche starten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Limit Dialog */}
      <Dialog open={showLimitDialog} onOpenChange={setShowLimitDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="limit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" /> Lead-Limit erreicht
            </DialogTitle>
            <DialogDescription>
              Sie haben Ihr monatliches Lead-Kontingent aufgebraucht.
            </DialogDescription>
          </DialogHeader>

          {usageData && (
            <div className="space-y-5 py-2">
              {/* Usage bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Verbrauch diesen Monat</span>
                  <span className="font-semibold">{usageData.leads_used_this_month} / {usageData.effective_limit} Leads</span>
                </div>
                <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: '100%' }} />
                </div>
                <p className="text-xs text-muted-foreground">
                  Aktueller Plan: <span className="font-medium">{usageData.plan_name}</span> ({usageData.max_leads_per_month} Leads/Monat
                  {usageData.addon_leads > 0 && ` + ${usageData.addon_leads} Add-on`})
                </p>
              </div>

              {/* Divider */}
              <div className="border-t" />

              {/* Option 1: Buy add-on */}
              <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-primary" />
                    <span className="font-semibold">Sofort {usageData.addon_amount} Leads dazu</span>
                  </div>
                  <span className="text-lg font-bold">{usageData.addon_price_eur.toFixed(2).replace('.', ',')} &euro;</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Einmalzahlung — gilt nur diesen Monat. Sofort nach Zahlung verfügbar.
                </p>
                <Button className="w-full" onClick={buyLeadAddon} disabled={addonLoading} data-testid="buy-addon-btn">
                  {addonLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  {usageData.addon_amount} Leads kaufen
                </Button>
              </div>

              {/* Option 2: Upgrade plan */}
              {usageData.next_plan && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <span className="font-semibold">Upgrade auf {usageData.next_plan.name}</span>
                    </div>
                    <span className="text-lg font-bold">{usageData.next_plan.monthly_eur.toFixed(2).replace('.', ',')} &euro;/Monat</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {usageData.next_plan.max_leads_per_month} Leads pro Monat — dauerhaft mehr Kapazität.
                  </p>
                  <Button variant="outline" className="w-full" onClick={() => { setShowLimitDialog(false); navigate('/billing'); }} data-testid="upgrade-plan-btn">
                    <TrendingUp className="w-4 h-4 mr-2" /> Zum Abo-Upgrade
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLimitDialog(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Search Warning Dialog */}
      <Dialog open={!!duplicateWarning} onOpenChange={(open) => { if (!open) { setDuplicateWarning(null); setPendingSearchBody(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="duplicate-warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" /> Suche bereits vorhanden
            </DialogTitle>
            <DialogDescription>
              Eine Suche mit identischen Filterkriterien wurde bereits durchgeführt.
            </DialogDescription>
          </DialogHeader>

          {duplicateWarning?.existingRun && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg bg-muted/50 border p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Vorherige Suche</span>
                  <Badge variant="secondary">
                    {new Date(duplicateWarning.existingRun.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-2 text-sm">
                  {duplicateWarning.existingRun.filters_region && (
                    <Badge variant="outline"><MapPin className="w-3 h-3 mr-1" />{duplicateWarning.existingRun.filters_region}</Badge>
                  )}
                  {duplicateWarning.existingRun.filters_industry && (
                    <Badge variant="outline"><Briefcase className="w-3 h-3 mr-1" />{duplicateWarning.existingRun.filters_industry}</Badge>
                  )}
                  {duplicateWarning.existingRun.filters_company_size && (
                    <Badge variant="outline"><Users className="w-3 h-3 mr-1" />{duplicateWarning.existingRun.filters_company_size}</Badge>
                  )}
                  {!duplicateWarning.existingRun.filters_region && !duplicateWarning.existingRun.filters_industry && (
                    <Badge variant="outline">Ohne Filter (Alle Regionen/Branchen)</Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{duplicateWarning.existingRun.accounts_found} Leads</span> wurden dabei gefunden.
                </p>
              </div>

              <div className="space-y-2">
                <Button className="w-full" onClick={() => {
                  selectRun(duplicateWarning.existingRun.id);
                  setDuplicateWarning(null);
                }} data-testid="view-existing-results-btn">
                  <Eye className="w-4 h-4 mr-2" /> Vorhandene Ergebnisse anzeigen
                </Button>
                <Button variant="outline" className="w-full" onClick={async () => {
                  const body = duplicateWarning.body;
                  setDuplicateWarning(null);
                  await tryStartDiscovery(body, true);
                }} data-testid="force-new-search-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Trotzdem neue Suche starten
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDuplicateWarning(null)}>Abbrechen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
